import { useQuery } from "@tanstack/react-query";
import api from "../api";

export interface Member {
  pikitup_member_id: number;
  member_first_name: string;
  member_surname: string;
  member_email_id: string;
  member_mobile_no: string;
  role_name: string;
  member_type_name: string;
  profile_status_flg: number;
  created_date: string;
}

interface MembersResponse {
  statusCode: number;
  message: string;
  data: {
    members: Member[];
    total: number; // total number of members (for pagination)
  };
}

// --- Reusable serializer ---
const serializeParams = (params: Record<string, any>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      searchParams.append(key, `[${value.join(",")}]`);
    } else if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

// --- Fetch members with pagination ---
const fetchMembers = async (
  page: number = 1,
  pageSize: number = 10,
  filters: Record<string, any> = {}
): Promise<{ members: Member[]; total: number }> => {
  try {
    const params: Record<string, any> = {
      client_type: "ADMIN",
      page,
      paginate: true,
      perPage: pageSize,
      ...filters,
    };

    const { data } = await api.get<MembersResponse>("/members/v1", {
      params,
      paramsSerializer: serializeParams,
    });

    return data?.data || { members: [], total: 0 };
  } catch (err: any) {
    if (err.response?.status === 404) {
      return { members: [], total: 0 };
    }
    console.error("Error fetching members:", err);
    return { members: [], total: 0 };
  }
};

// --- Hook ---
export const useMembers = (
  page: number,
  pageSize: number,
  filters: Record<string, any>
) => {
  const filtersKey = JSON.stringify(filters);

  return useQuery({
    queryKey: ["members", page, pageSize, filtersKey],
    queryFn: () => fetchMembers(page, pageSize, filters),
    keepPreviousData: true,
  });
};
